import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { Activity, ArrowUpRight, ArrowDownRight } from "lucide-react";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Positions() {
  const [data, setData] = useState({ positions: [], total_pnl: 0, source: "derived" });
  const [live, setLive] = useState(true);
  const timer = useRef(null);

  const load = () => api.get("/trading/positions").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    if (live) timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, [live]);

  const total = data.total_pnl || 0;

  return (
    <Layout>
      <PageHeader title="Live Positions" subtitle="Auto-updating open positions & P&L ticker"
        right={
          <button data-testid="toggle-live" onClick={() => setLive((v) => !v)}
            className={`h-9 px-4 text-sm font-semibold border flex items-center gap-2 transition-colors ${live ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500"}`}>
            <Activity className={`w-4 h-4 ${live ? "animate-pulse" : ""}`} /> {live ? "Live" : "Paused"}
          </button>
        } />
      <div className="p-8">
        <div className="border border-slate-200 bg-white p-6 mb-8 flex items-end justify-between">
          <div>
            <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-1">Total Unrealized P&L</div>
            <div data-testid="ticker-total" className={`font-mono text-4xl font-medium tracking-tight transition-colors duration-300 ${total >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {total >= 0 ? "+" : ""}₹{fmt(total)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-slate-400">Source</div>
            <div className="font-mono text-sm">{data.source === "broker_live" ? "BROKER LIVE" : "DERIVED"}</div>
            <div data-testid="market-status" className={`text-xs font-mono mt-1 ${data.market_status === "open" ? "text-emerald-600" : "text-slate-400"}`}>
              MKT {(data.market_status || "").toUpperCase() || "—"}
            </div>
            <div className="text-xs text-slate-400 mt-1">{data.positions?.length || 0} open</div>
          </div>
        </div>

        {(data.positions || []).length === 0 ? (
          <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400">
            <pre className="font-mono text-xs mb-3">╭──────────╮{"\n"}│  FLAT   │{"\n"}╰──────────╯</pre>
            No open positions. Deploy a strategy and fire a signal to see the live ticker move.
          </div>
        ) : (
          <div className="border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3">Symbol</th><th className="px-6 py-3">Side</th>
                <th className="px-6 py-3 text-right">Qty</th><th className="px-6 py-3 text-right">Avg</th>
                <th className="px-6 py-3 text-right">LTP</th><th className="px-6 py-3 text-right">Unrealized P&L</th>
                <th className="px-6 py-3">Mode</th></tr></thead>
              <tbody>
                {data.positions.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`position-row-${i}`}>
                    <td className="px-6 py-2.5 font-mono">{p.symbol || p.tradingsymbol}</td>
                    <td className="px-6 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${p.side === "BUY" ? "text-emerald-600" : "text-rose-600"}`}>
                        {p.side === "BUY" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{p.side || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-2.5 text-right font-mono">{p.qty || p.quantity}</td>
                    <td className="px-6 py-2.5 text-right font-mono">₹{fmt(p.avg_price ?? p.average_price)}</td>
                    <td className="px-6 py-2.5 text-right font-mono">₹{fmt(p.ltp ?? p.last_price)}</td>
                    <td className={`px-6 py-2.5 text-right font-mono transition-colors duration-300 ${(p.unrealized_pnl ?? p.pnl) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {(p.unrealized_pnl ?? p.pnl) >= 0 ? "+" : ""}₹{fmt(p.unrealized_pnl ?? p.pnl)}
                    </td>
                    <td className="px-6 py-2.5 text-xs uppercase text-slate-500">{p.mode || "live"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
